import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const UserDetailModal = ({ user, isOpen, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    department: user?.department || '',
    role: user?.role || '',
    status: user?.status || 'active'
  });

  const [rolePermissions, setRolePermissions] = useState({
    createVaults: true,
    editVaults: true,
    deleteVaults: false,
    shareVaults: true,
    exportData: false,
    viewAuditLogs: false,
    manageUsers: false,
    offlineAccess: true,
    apiAccess: false
  });

  const departmentOptions = [
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Sales', label: 'Sales' },
    { value: 'HR', label: 'Human Resources' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Legal', label: 'Legal' }
  ];

  const roleOptions = [
    { value: 'Super Admin', label: 'Super Admin' },
    { value: 'Department Manager', label: 'Department Manager' },
    { value: 'Contributor', label: 'Contributor' },
    { value: 'Viewer', label: 'Viewer' },
    { value: 'Auditor', label: 'Auditor' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const mockAuditLogs = [
    { id: 1, action: 'Login', timestamp: '2025-01-03 14:30:00', ip: '192.168.1.100', device: 'Chrome on Windows' },
    { id: 2, action: 'Vault Access', timestamp: '2025-01-03 14:25:00', ip: '192.168.1.100', device: 'Chrome on Windows' },
    { id: 3, action: 'Password Updated', timestamp: '2025-01-03 10:15:00', ip: '192.168.1.100', device: 'Chrome on Windows' },
    { id: 4, action: 'Shared Vault', timestamp: '2025-01-02 16:45:00', ip: '192.168.1.100', device: 'Chrome on Windows' },
    { id: 5, action: 'Login', timestamp: '2025-01-02 09:00:00', ip: '192.168.1.100', device: 'Chrome on Windows' }
  ];

  const mockDevices = [
    { id: 1, name: 'Chrome on Windows', lastUsed: '2025-01-03 14:30:00', trusted: true, location: 'New York, US' },
    { id: 2, name: 'Safari on iPhone', lastUsed: '2025-01-02 18:20:00', trusted: true, location: 'New York, US' },
    { id: 3, name: 'Firefox on macOS', lastUsed: '2024-12-28 11:15:00', trusted: false, location: 'San Francisco, US' }
  ];

  const mockVaultAccess = [
    { id: 1, name: 'Engineering Secrets', role: 'Contributor', lastAccessed: '2025-01-03 14:25:00' },
    { id: 2, name: 'Shared Credentials', role: 'Viewer', lastAccessed: '2025-01-02 16:45:00' },
    { id: 3, name: 'API Keys', role: 'Contributor', lastAccessed: '2025-01-01 10:30:00' }
  ];

  if (!isOpen || !user) return null;

  const handleSave = () => {
    onSave({ ...user, ...formData, rolePermissions });
    setEditMode(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name,
      email: user?.email,
      department: user?.department,
      role: user?.role,
      status: user?.status
    });
    setEditMode(false);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'permissions', label: 'Permissions', icon: 'Shield' },
    { id: 'vaults', label: 'Vault Access', icon: 'Vault' },
    { id: 'devices', label: 'Devices', icon: 'Smartphone' },
    { id: 'audit', label: 'Audit Log', icon: 'FileText' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-primary-foreground">
                {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{user?.name}</h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {editMode ? (
              <>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  Save Changes
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setEditMode(true)}
                iconName="Edit"
                iconPosition="left"
                iconSize={16}
              >
                Edit User
              </Button>
            )}
            
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Full Name"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  disabled={!editMode}
                  required
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  disabled={!editMode}
                  required
                />
                
                <Select
                  label="Department"
                  options={departmentOptions}
                  value={formData?.department}
                  onChange={(value) => setFormData({ ...formData, department: value })}
                  disabled={!editMode}
                />
                
                <Select
                  label="Role"
                  options={roleOptions}
                  value={formData?.role}
                  onChange={(value) => setFormData({ ...formData, role: value })}
                  disabled={!editMode}
                />
                
                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => setFormData({ ...formData, status: value })}
                  disabled={!editMode}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-border">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">12</p>
                  <p className="text-sm text-muted-foreground">Vaults Access</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">3</p>
                  <p className="text-sm text-muted-foreground">Trusted Devices</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">45</p>
                  <p className="text-sm text-muted-foreground">Login Sessions</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-foreground mb-4">Role Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(rolePermissions)?.map(([key, value]) => (
                    <Checkbox
                      key={key}
                      label={key?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase())}
                      checked={value}
                      onChange={(e) => setRolePermissions({ ...rolePermissions, [key]: e?.target?.checked })}
                      disabled={!editMode}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'vaults' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">Vault Access</h3>
              <div className="space-y-3">
                {mockVaultAccess?.map((vault) => (
                  <div key={vault?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="Vault" size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground">{vault?.name}</p>
                        <p className="text-sm text-muted-foreground">Last accessed: {vault?.lastAccessed}</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-accent text-accent-foreground rounded text-sm font-medium">
                      {vault?.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'devices' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">Trusted Devices</h3>
              <div className="space-y-3">
                {mockDevices?.map((device) => (
                  <div key={device?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="Smartphone" size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground">{device?.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {device?.location} • Last used: {device?.lastUsed}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {device?.trusted ? (
                        <span className="px-2 py-1 bg-success text-success-foreground rounded text-xs">
                          Trusted
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-warning text-warning-foreground rounded text-xs">
                          Untrusted
                        </span>
                      )}
                      <Button variant="ghost" size="icon">
                        <Icon name="MoreHorizontal" size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">Recent Activity</h3>
              <div className="space-y-3">
                {mockAuditLogs?.map((log) => (
                  <div key={log?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="Activity" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">{log?.action}</p>
                        <p className="text-sm text-muted-foreground">
                          {log?.device} • {log?.ip}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">{log?.timestamp}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;